<template>
  <div class="template-mail-form">
    <form
      @keydown.enter.prevent="() => {}"
      @submit.prevent="handleSubmit"
      ref="template_form"
    >
      <!-- ======================================== -->
      <!--         SECTION 1 : INFOS GENERALES      -->
      <!-- ======================================== -->
      <div class="form-section">
        <div class="form-section-header" @click="sections.general = !sections.general">
          <h4><i class="fal fa-info-circle"></i> {{ $t("TEMPLATES_MAILS.GENERAL_INFO") }}</h4>
          <i :class="sections.general ? 'fal fa-chevron-up' : 'fal fa-chevron-down'"></i>
        </div>
        <div class="form-section-body" v-show="sections.general">
          <div class="form-grid">
            <!-- Organization -->
            <div
              class="form-grid-item"
              v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)"
            >
              <base-input :label="`${$t('COMMON.ORGANIZATION')} (*)`">
                <organization-selector
                  :allowNone="false"
                  :disabled="!!templateMail.id"
                  :filterable="true"
                  :organization="templateMail.organization?.id ?? null"
                  :showAll="false"
                  @organizationChanged="organizationChanged"
                />
              </base-input>
            </div>

            <!-- Type -->
            <div class="form-grid-item">
              <base-input :label="`${$t('TEMPLATES_MAILS.TYPE')} (*)`">
                <type-mail-selector
                  :filterable="true"
                  :showAll="false"
                  :typeMail="templateMail.typeMail.id"
                  @typeMailChanged="typeMailChanged"
                />
              </base-input>
              <validation-error :errors="apiValidationErrors.typeMail" />
            </div>

            <!-- Titles per locale -->
            <div
              class="form-grid-item"
              v-for="(locale, key) in localeOptions"
              :key="'title-' + key"
            >
              <base-input
                :label="`${$t('TEMPLATES_MAILS.TITLE')} (*) — ${locale.toUpperCase()}`"
                v-model="templateMail.title[locale]"
                @change="onFormChanged()"
              />
              <validation-error v-if="locale === 'fr'" :errors="apiValidationErrors.title" />
            </div>

            <!-- Subjects per locale -->
            <div
              class="form-grid-item"
              v-for="(locale, key) in localeOptions"
              :key="'subject-' + key"
            >
              <base-input
                :label="`${$t('TEMPLATES_MAILS.SUBJECT')} (*) — ${locale.toUpperCase()}`"
                v-model="templateMail.subject[locale]"
                @change="onFormChanged()"
              />
              <validation-error v-if="locale === 'fr'" :errors="apiValidationErrors.subject" />
            </div>

            <!-- Default switch -->
            <div class="form-grid-item form-grid-item--switch">
              <el-switch
                v-model="templateMail.is_default"
                :active-text="$t('TEMPLATES_MAILS.IS_DEFAULT_TEMPLATE')"
                active-color="#576654"
                inactive-color="#E5E7EB"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- ======================================== -->
      <!--      SECTION 2 : EXPEDITEUR / DEST       -->
      <!-- ======================================== -->
      <div class="form-section">
        <div class="form-section-header" @click="sections.recipients = !sections.recipients">
          <h4><i class="fal fa-envelope"></i> {{ $t("TEMPLATES_MAILS.SENDER_RECIPIENTS") }}</h4>
          <i :class="sections.recipients ? 'fal fa-chevron-up' : 'fal fa-chevron-down'"></i>
        </div>
        <div class="form-section-body" v-show="sections.recipients">
          <div class="form-grid">
            <div class="form-grid-item">
              <base-input :label="`${$t('TEMPLATES_MAILS.FROM_EMAIL')} (*)`" v-model="templateMail.from.email" @change="onFormChanged()" />
            </div>
            <div class="form-grid-item">
              <base-input :label="`${$t('TEMPLATES_MAILS.FROM_NAME')} (*)`" v-model="templateMail.from.name" @change="onFormChanged()" />
            </div>
            <div class="form-grid-item">
              <base-input :label="$t('TEMPLATES_MAILS.REPLY_TO_EMAIL')" v-model="templateMail.reply_to.email" @change="onFormChanged()" />
            </div>
            <div class="form-grid-item">
              <base-input :label="$t('TEMPLATES_MAILS.REPLY_TO_NAME')" v-model="templateMail.reply_to.name" @change="onFormChanged()" />
            </div>
            <div class="form-grid-item form-grid-item--full">
              <tags-input :label="$t('TEMPLATES_MAILS.TO')" v-model="templateMail.recipients" @change="(mails) => { selectedUsersTo = mails; }" />
            </div>
            <div class="form-grid-item">
              <tags-input :label="$t('TEMPLATES_MAILS.CC')" v-model="templateMail.cc" @change="(mails) => { selectedUsersCc = mails; }" />
            </div>
            <div class="form-grid-item">
              <tags-input :label="$t('TEMPLATES_MAILS.BCC')" v-model="templateMail.bcc" @change="(mails) => { selectedUsersBcc = mails; }" />
            </div>
          </div>
        </div>
      </div>

      <!-- ======================================== -->
      <!--        SECTION 3 : VARIABLES              -->
      <!-- ======================================== -->
      <div
        class="form-section"
        v-if="typeVariables && Object.keys(typeVariables).length"
      >
        <div class="form-section-header" @click="sections.variables = !sections.variables">
          <h4><i class="fal fa-code"></i> {{ $t("TEMPLATES_MAILS.AVAILABLE_VARIABLES") }}</h4>
          <i :class="sections.variables ? 'fal fa-chevron-up' : 'fal fa-chevron-down'"></i>
        </div>
        <div class="form-section-body" v-show="sections.variables">
          <p class="variables-hint">{{ $t("TEMPLATES_MAILS.VARIABLES_HINT") }}</p>
          <div class="variables-panel">
            <span
              v-for="(label, key) in typeVariables"
              :key="key"
              class="variable-badge"
              @click="insertVariable(key)"
              :title="label"
            >
              <i class="fal fa-plus-circle"></i> [{{ key }}]
            </span>
          </div>
        </div>
      </div>

      <!-- ======================================== -->
      <!--        SECTION 4 : EMAIL BUILDER          -->
      <!-- ======================================== -->
      <div class="form-section form-section--builder">
        <div class="form-section-header form-section-header--builder">
          <h4>
            <i class="fal fa-paint-brush"></i>
            {{ $t("TEMPLATES_MAILS.EMAIL_BUILDER") }}
          </h4>
          <div class="builder-lang-tabs">
            <button
              v-for="(locale, key) in localeOptions"
              :key="key"
              type="button"
              class="lang-tab"
              :class="{ 'lang-tab--active': language === locale }"
              @click="switchLanguage(locale)"
            >
              {{ locale.toUpperCase() }}
            </button>
          </div>
        </div>
        <div class="gjs-container">
          <div id="gjs-editor" ref="gjsEditor"></div>
        </div>
      </div>

      <!-- ======================================== -->
      <!--            BOUTONS ACTIONS                -->
      <!-- ======================================== -->
      <div class="form-actions">
        <button type="button" class="btn cancel" :disabled="loading" @click="onCloseTemplateMail">
          {{ $t("COMMON.CANCEL") }}
        </button>
        <base-button
          type="button"
          class="btn btn-sm elite-submit"
          native-type="submit"
          :disabled="loading"
        >
          <i class="fas fa-spinner fa-spin" v-if="loading"></i>
          {{ templateMail.id ? $t("TEMPLATES_MAILS.EDIT_TEMPLATE_MAIL") : $t("TEMPLATES_MAILS.ADD_TEMPLATE_MAIL") }}
        </base-button>
      </div>
    </form>
  </div>
</template>

<script>
import { cloneDeep, debounce } from "lodash";
import { Select, Option, Switch } from "element-ui";
import grapesjs from "grapesjs/dist/grapes.min.js";
import grapesjsPresetNewsletter from "grapesjs-preset-newsletter";
import "grapesjs/dist/css/grapes.min.css";
import formMixin from "@/mixins/form-mixin";
import ValidationError from "@/components/ValidationError.vue";
import BaseInput from "@/components/Inputs/BaseInput.vue";
import TagsInput from "@/components/Inputs/TagsInput.vue";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import TypeMailSelector from "@/components/TypeMailSelector.vue";
import { mapGetters } from "vuex";
import { LOCALE_FR, localeOptions } from "@/constants/templateMail";
import defaultTemplateMail from "../defaultTemplateMail.js";

export default {
  layout: "DashboardLayout",

  components: {
    BaseInput,
    TagsInput,
    ValidationError,
    OrganizationSelector,
    TypeMailSelector,
    [Select.name]: Select,
    [Option.name]: Option,
    [Switch.name]: Switch,
  },

  mixins: [formMixin],

  props: ["templateMailData", "formErrors", "loading"],

  data() {
    let templateMail = cloneDeep(this.templateMailData);
    templateMail = this.$fillUserOrganizationData(templateMail);
    const language = LOCALE_FR;

    if (!templateMail.html_content) {
      templateMail.html_content = {};
      Object.values(localeOptions).forEach((locale) => {
        templateMail.html_content[locale] = "";
      });
    }

    // Store GrapesJS project data per locale (components + styles)
    const gjsProjectData = {};
    Object.values(localeOptions).forEach((locale) => {
      gjsProjectData[locale] = null;
    });

    return {
      templateMail,
      gjsEditor: null,
      gjsProjectData,
      typeVariables: [],
      selectedUsersTo: templateMail.recipients ?? [],
      selectedUsersCc: templateMail.cc ?? [],
      selectedUsersBcc: templateMail.bcc ?? [],
      language,
      localeOptions,
      sections: {
        general: true,
        recipients: false,
        variables: true,
      },
    };
  },

  computed: {
    ...mapGetters("organizations", { globalOrganization: "globalOrganization" }),
    ...mapGetters("organizationOptions", { organizationConfig: "organizationConfig" }),
    ...mapGetters("profile", { me: "me" }),
  },

  created() {
    this.selectedUsersTo = this.templateMail.recipients ?? [];
    this.selectedUsersCc = this.templateMail.cc ?? [];
    this.selectedUsersBcc = this.templateMail.bcc ?? [];
    if (this.templateMail.typeMail?.id) {
      this.typeVariables = this.templateMail.typeMail.variables;
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.initGrapesJs();
    });
  },

  beforeDestroy() {
    if (this.gjsEditor) {
      this.gjsEditor.destroy();
    }
  },

  methods: {
    initGrapesJs() {
      const initialHtml = this.templateMail.html_content?.[this.language] || "";

      this.gjsEditor = grapesjs.init({
        container: this.$refs.gjsEditor,
        fromElement: false,
        height: "100%",
        width: "auto",
        storageManager: false,
        plugins: [grapesjsPresetNewsletter],
        pluginsOpts: {
          [grapesjsPresetNewsletter]: {
            modalTitleImport: "Import HTML",
            modalTitleExport: "Export HTML",
          },
        },
        deviceManager: {
          devices: [
            { name: "Desktop", width: "" },
            { name: "Tablet", width: "768px", widthMedia: "992px" },
            { name: "Mobile", width: "370px", widthMedia: "480px" },
          ],
        },
        canvas: {
          styles: [
            "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap",
          ],
        },
      });

      if (initialHtml) {
        this.gjsEditor.setComponents(initialHtml);
        // Save initial project data so switching back preserves styles
        this.$nextTick(() => {
          this.gjsProjectData[this.language] = this.gjsEditor.getProjectData();
        });
      }

      this.registerCustomBlocks();

      this.gjsEditor.on(
        "change:changesCount",
        debounce(() => {
          this.syncEditorToModel();
        }, 600)
      );
    },

    registerCustomBlocks() {
      if (!this.gjsEditor) return;
      const bm = this.gjsEditor.BlockManager;

      // Variable blocks
      if (this.typeVariables && Object.keys(this.typeVariables).length) {
        Object.entries(this.typeVariables).forEach(([key, label]) => {
          bm.add(`var-${key}`, {
            label: `[${key}]`,
            category: "Variables",
            content: {
              type: "text",
              content: `[${key}]`,
              style: {
                display: "inline-block",
                padding: "2px 6px",
                background: "#e3f2fd",
                color: "#1565c0",
                "border-radius": "3px",
                "font-family": "monospace",
                "font-size": "13px",
              },
            },
          });
        });
      }

      // Custom email blocks
      bm.add("button-primary", {
        label: "Bouton",
        category: "Basique",
        content: `<a href="#" style="display:inline-block;padding:12px 30px;background-color:#EF8152;color:#ffffff;text-decoration:none;border-radius:5px;font-family:Arial,sans-serif;font-size:14px;text-align:center;margin:10px 0;">Bouton</a>`,
      });

      bm.add("divider-block", {
        label: "Séparateur",
        category: "Basique",
        content: `<hr style="border:0;border-top:1px solid #dee2e6;margin:20px 0;" />`,
      });

      bm.add("spacer-block", {
        label: "Espace",
        category: "Basique",
        content: `<div style="height:30px;"></div>`,
      });
    },

    syncEditorToModel() {
      if (!this.gjsEditor) return;
      const html = this.gjsEditor.getHtml();
      const css = this.gjsEditor.getCss();
      const fullHtml = css ? `<style>${css}</style>${html}` : html;
      this.$set(this.templateMail.html_content, this.language, fullHtml);
      // Save project data (components + styles) for this locale
      this.gjsProjectData[this.language] = this.gjsEditor.getProjectData();
    },

    switchLanguage(locale) {
      if (locale === this.language) return;
      // Save current locale (project data + html)
      this.syncEditorToModel();
      // Switch
      const oldLang = this.language;
      this.language = locale;

      if (!this.gjsEditor) return;

      // Restore from project data if available (preserves styles)
      if (this.gjsProjectData[locale]) {
        this.gjsEditor.loadProjectData(this.gjsProjectData[locale]);
      } else {
        // First time loading this locale — use raw html_content
        const content = this.templateMail.html_content?.[locale] || "";
        this.gjsEditor.setComponents(content);
      }
    },

    insertVariable(key) {
      if (!this.gjsEditor) return;
      const selected = this.gjsEditor.getSelected();
      if (selected && selected.is("text")) {
        const content = selected.get("content") || "";
        selected.set("content", content + `[${key}]`);
      } else {
        this.gjsEditor.addComponents({
          type: "text",
          content: `[${key}]`,
          style: {
            display: "inline-block",
            padding: "2px 6px",
            background: "#e3f2fd",
            color: "#1565c0",
            "border-radius": "3px",
            "font-family": "monospace",
            "font-size": "13px",
          },
        });
      }
    },

    async handleSubmit() {
      this.syncEditorToModel();

      this.templateMail.recipients = this.selectedUsersTo.map((i) => i.item || i);
      this.templateMail.cc = this.selectedUsersCc.map((i) => i.item || i);
      this.templateMail.bcc = this.selectedUsersBcc.map((i) => i.item || i);
      this.templateMail.content = "content test";

      // Ensure style fields have default values (required by backend)
      const defaults = cloneDeep(defaultTemplateMail);
      const styleFields = [
        "background_color",
        "title_style",
        "text_style",
        "buttons_colors",
        "links_style",
        "footer_style",
        "sections",
      ];
      styleFields.forEach((field) => {
        if (!this.templateMail[field]) {
          this.templateMail[field] = defaults[field];
        }
      });

      let templateMailData = cloneDeep(this.templateMail);
      templateMailData = this.$fillUserOrganizationData(templateMailData);
      this.$emit("templateMailSubmitted", templateMailData);
    },

    typeMailChanged(typeMailId, typeMail) {
      this.templateMail.typeMail = typeMail;
      this.typeVariables = typeMail?.variables ?? [];

      if (typeMail.id) {
        this.templateMail.sections = typeMail.sections;
        this.templateMail.title = cloneDeep(typeMail.label_locale);
        this.templateMail.subject = cloneDeep(typeMail.label_locale);
      }

      this.registerCustomBlocks();
      this.onFormChanged();
    },

    organizationChanged(organizationId, organization) {
      this.templateMail = {
        ...this.templateMail,
        organization: organization,
        logo: organization.logo,
        from: { email: organization?.email ?? "", name: organization?.name ?? "" },
        reply_to: { email: organization?.email ?? "", name: organization?.name ?? "" },
      };
      this.onFormChanged();
    },

    onFormChanged() {
      this.$emit("formChanged");
    },

    onCloseTemplateMail() {
      this.$emit("onCloseTemplateMailModal");
    },
  },

  watch: {
    formErrors(errors) {
      if (errors) this.setApiValidation(errors);
    },
    templateMailData(data) {
      if (data) {
        this.templateMail = { ...this.templateMail, ...cloneDeep(data) };
        if (this.gjsEditor && this.templateMail.html_content?.[this.language]) {
          this.gjsEditor.setComponents(this.templateMail.html_content[this.language]);
        }
      }
    },
  },
};
</script>

<style>
/* ============================================
   TEMPLATE MAIL FORM - LAYOUT
   ============================================ */
.template-mail-form {
  display: flex;
  flex-direction: column;
}

/* Sections */
.form-section {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  background: #fff;
}

.form-section--builder {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.form-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9fa;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid #e9ecef;
  transition: background 0.15s;
}

.form-section-header:hover {
  background: #f0f1f3;
}

.form-section-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-section-header h4 i {
  color: #576654;
  width: 18px;
}

.form-section-header--builder {
  cursor: default;
}

.form-section-header--builder:hover {
  background: #f8f9fa;
}

.form-section-body {
  padding: 16px;
}

/* Form grid */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 20px;
}

.form-grid-item--full {
  grid-column: 1 / -1;
}

.form-grid-item--switch {
  display: flex;
  align-items: center;
  padding-top: 24px;
}

/* Language tabs on builder header */
.builder-lang-tabs {
  display: flex;
  gap: 4px;
  margin-left: auto;
}

.lang-tab {
  padding: 5px 14px;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.15s;
}

.lang-tab:hover {
  background: #f0f1f3;
  border-color: #576654;
  color: #576654;
}

.lang-tab--active {
  background: #576654;
  border-color: #576654;
  color: #fff;
}

.lang-tab--active:hover {
  background: #4a5a46;
  color: #fff;
}

/* Variables panel */
.variables-hint {
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 10px;
}

.variables-panel {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.variable-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
  border-radius: 6px;
  font-family: "Fira Code", "Courier New", monospace;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
}

.variable-badge:hover {
  background: #1565c0;
  color: #fff;
  border-color: #1565c0;
}

.variable-badge i {
  font-size: 11px;
}

/* GrapesJS container */
.gjs-container {
  height: 700px;
  position: relative;
}

#gjs-editor {
  height: 100% !important;
  border: none !important;
}

/* GrapesJS theme overrides */
.gjs-one-bg {
  background-color: #f8f9fa !important;
}

.gjs-two-color {
  color: #495057 !important;
}

.gjs-three-bg {
  background-color: #576654 !important;
}

.gjs-four-color,
.gjs-four-color-h:hover {
  color: #576654 !important;
}

/* Form actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 0;
  border-top: 1px solid #e9ecef;
  margin-top: 12px;
}
</style>
